import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { AlertTriangle, Info, CheckCircle2, AlertCircle, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils/classNames';

/**
 * GGC V2. Alert / Banner — Figma node 166:233 family (Component 02).
 *
 * Inline banner used inside a page (not a toast). 4 tone variants for
 * Warning / Info / Success / Error. Icon + message in a single row.
 *
 * Spec: padding 10x14, r-md, font 12px Medium. Warning uses solid border;
 * other tones use the matching `-light` color as border for a softer look.
 */
const ggcAlertVariants = cva(
  'inline-flex w-full items-center gap-[9px] rounded-ggc-md border px-[14px] py-[10px] font-ggc text-[12px] font-medium',
  {
    variants: {
      tone: {
        warning: 'bg-ggc-sub-yellow-light border-ggc-sub-yellow-light text-ggc-orange',
        info: 'bg-ggc-blue-light border-ggc-blue-light text-ggc-blue',
        success: 'bg-ggc-green-light border-ggc-green-light text-ggc-green',
        error: 'bg-ggc-red-light border-ggc-red-light text-ggc-red',
      },
    },
    defaultVariants: {
      tone: 'info',
    },
  }
);

const iconMap: Record<NonNullable<GgcAlertProps['tone']>, LucideIcon> = {
  warning: AlertTriangle,
  info: Info,
  success: CheckCircle2,
  error: AlertCircle,
};

export interface GgcAlertProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof ggcAlertVariants> {
  /** Icon glyph override; falls back to the tone's default. */
  icon?: React.ReactNode;
  /** Hide the leading icon entirely. */
  hideIcon?: boolean;
}

const GgcAlert = React.forwardRef<HTMLDivElement, GgcAlertProps>(
  ({ className, tone, icon, hideIcon, children, ...props }, ref) => {
    const resolvedTone = tone ?? 'info';
    const DefaultIcon = iconMap[resolvedTone];
    return (
      <div
        ref={ref}
        role="status"
        className={cn(ggcAlertVariants({ tone: resolvedTone }), className)}
        {...props}
      >
        {!hideIcon && (
          <span aria-hidden className="flex shrink-0 items-center justify-center">
            {icon ?? <DefaultIcon className="size-4 shrink-0" />}
          </span>
        )}
        <span className="flex-1 leading-[1.5]">{children}</span>
      </div>
    );
  }
);
GgcAlert.displayName = 'GgcAlert';

export { GgcAlert, ggcAlertVariants };
